// NEW_FILE_JUSTIFIED: New messages feature component
import { memo } from 'react';
import { Users } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { IConversationItemProps, TMessageChannel } from '@/@types/messages';

const CHANNEL_LABELS: Record<TMessageChannel, string> = {
  sms: 'SMS',
  whatsapp: 'WhatsApp',
  email: 'Email',
  instagram: 'Instagram',
  facebook: 'Facebook',
  telegram: 'Telegram',
};

function ConversationItemBase({ conversation, isSelected, onSelect }: IConversationItemProps) {
  const {
    id,
    contactName,
    contactInitials,
    avatarColor,
    lastMessage,
    lastMessageTime,
    unreadCount,
    channel,
  } = conversation;

  const hasUnread = unreadCount > 0;

  function handleClick() {
    onSelect(id);
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-current={isSelected ? 'true' : undefined}
      className={cn(
        'w-full flex items-center gap-3 text-left transition-colors overflow-hidden',
        'px-4 py-3',
        isSelected
          ? 'bg-msg-active-conversation border-l-[3px] border-msg-active-border'
          : 'border-l-[3px] border-transparent hover:bg-muted/30'
      )}
    >
      {/* Avatar */}
      {conversation.profilePicUrl !== undefined ? (
        <img
          src={conversation.profilePicUrl}
          alt={contactName}
          className="w-10 h-10 rounded-full object-cover shrink-0"
        />
      ) : (
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
          style={{ backgroundColor: avatarColor }}
        >
          {conversation.chatType === 'group' ? (
            <Users size={18} className="text-primary-foreground" />
          ) : (
            <span className="text-sm font-semibold text-primary-foreground">{contactInitials}</span>
          )}
        </div>
      )}

      {/* Content */}
      <div className="flex-1 min-w-0">
        {/* Name and time row */}
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm font-semibold text-foreground truncate min-w-0">
            {contactName}
          </span>
          <span className="text-xs text-foreground-placeholder shrink-0">{lastMessageTime}</span>
        </div>

        {/* Preview text and channel tag */}
        <div className="flex items-center gap-2 mt-0.5">
          <p className="text-xs text-muted-foreground truncate flex-1">{lastMessage}</p>
          <span className="text-xs text-muted-foreground bg-muted rounded px-1.5 py-0.5 shrink-0 font-medium">
            {CHANNEL_LABELS[channel]}
          </span>
        </div>
      </div>

      {/* Unread badge */}
      {hasUnread && (
        <div
          aria-hidden="true"
          className="w-5 h-5 rounded-full bg-primary flex items-center justify-center shrink-0"
        >
          <span className="text-[11px] font-semibold text-primary-foreground">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        </div>
      )}
      {hasUnread && <span className="sr-only">{unreadCount} unread</span>}
    </button>
  );
}

export const ConversationItem = memo(ConversationItemBase);
